import React, { useState, useEffect } from 'react';
import api from '../services/api';
import './UsersManagement.css';

const UsersManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('ALL');
  const [userLimitEdits, setUserLimitEdits] = useState({});
  const [bulkDomain, setBulkDomain] = useState('');
  const [bulkEmails, setBulkEmails] = useState('');
  const [bulkLimit, setBulkLimit] = useState('2');
  const [isApplyingBulk, setIsApplyingBulk] = useState(false);
  const [savingUserId, setSavingUserId] = useState(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      setMessage('Error loading users');
      setTimeout(() => setMessage(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = 
      user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.lastName?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesRole = filterRole === 'ALL' || user.role === filterRole;
    
    return matchesSearch && matchesRole;
  });

  const getRoleBadge = (role) => {
    const colors = {
      'ADMIN': 'red',
      'USER': 'blue',
      'INSTRUCTOR': 'green'
    };
    const color = colors[role] || 'gray';
    return (
      <span className={`role-badge ${color}`}>
        {role}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getUserLimitValue = (user) => {
    if (userLimitEdits[user.id] !== undefined) {
      return userLimitEdits[user.id];
    }
    return user.freeInterviewsLimitOverride ?? '';
  };

  const saveUserLimit = async (userId, overrideValue) => {
    try {
      setSavingUserId(userId);
      const rawValue = overrideValue !== undefined ? overrideValue : userLimitEdits[userId];
      const limit = rawValue === '' || rawValue === null || rawValue === undefined
        ? null
        : Number(rawValue);
      await api.put(`/api/users/${userId}/ai-interviews/free-limit`, {
        freeInterviewsLimitOverride: limit
      });
      setUsers(prev => prev.map(u => (
        u.id === userId ? { ...u, freeInterviewsLimitOverride: limit } : u
      )));
      setMessage('✅ Free interview limit updated');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error updating user limit:', error);
      setMessage('❌ Failed to update user limit');
      setTimeout(() => setMessage(''), 3000);
    } finally {
      setSavingUserId(null);
    }
  };

  const applyBulkDomain = async () => {
    try {
      setIsApplyingBulk(true);
      const limit = bulkLimit === '' ? null : Number(bulkLimit);
      await api.put('/api/users/ai-interviews/free-limit/domain', {
        domain: bulkDomain.trim(),
        freeInterviewsLimitOverride: limit
      });
      await fetchUsers();
      setMessage('✅ Updated users for domain');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error bulk updating by domain:', error);
      setMessage('❌ Failed to update users for domain');
      setTimeout(() => setMessage(''), 3000);
    } finally {
      setIsApplyingBulk(false);
    }
  };

  const applyBulkEmails = async () => {
    try {
      setIsApplyingBulk(true);
      const emails = bulkEmails
        .split(/[\n,]+/)
        .map(e => e.trim())
        .filter(Boolean);
      const limit = bulkLimit === '' ? null : Number(bulkLimit);
      await api.put('/api/users/ai-interviews/free-limit/emails', {
        emails,
        freeInterviewsLimitOverride: limit
      });
      await fetchUsers();
      setMessage('✅ Updated users by email list');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error bulk updating by emails:', error);
      setMessage('❌ Failed to update users by email list');
      setTimeout(() => setMessage(''), 3000);
    } finally {
      setIsApplyingBulk(false);
    }
  };

  if (loading) {
    return (
      <div className="users-management">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <div className="loading-text">Loading users...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="users-management">
      <div className="management-header">
        <h2>👥 Users Management</h2>
        <div className="header-stats">
          <span>Total Users: {users.length}</span>
        </div>
      </div>

      {message && (
        <div className="message">
          {message}
        </div>
      )}

      {/* AI Interview Overrides */}
      <div className="filters-section" style={{ alignItems: 'flex-start', flexDirection: 'column', gap: '12px' }}>
        <div style={{ fontWeight: 600 }}>🎯 AI Interview Free Limit Overrides</div>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <input
            type="text"
            placeholder="College domain (e.g. mriirs.edu)"
            value={bulkDomain}
            onChange={(e) => setBulkDomain(e.target.value)}
            className="search-input"
            style={{ minWidth: '260px' }}
          />
          <input
            type="number"
            placeholder="Free interviews"
            value={bulkLimit}
            onChange={(e) => setBulkLimit(e.target.value)}
            className="search-input"
            style={{ width: '160px' }}
          />
          <button
            className="filter-btn active"
            onClick={applyBulkDomain}
            disabled={isApplyingBulk}
          >
            Apply to Domain
          </button>
        </div>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <textarea
            placeholder="Emails (comma or new line separated)"
            value={bulkEmails}
            onChange={(e) => setBulkEmails(e.target.value)}
            className="search-input"
            style={{ minWidth: '360px', minHeight: '70px' }}
          />
          <button
            className="filter-btn active"
            onClick={applyBulkEmails}
            disabled={isApplyingBulk}
          >
            Apply to Emails
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search by username, email, or name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="filter-buttons">
          <button
            className={`filter-btn ${filterRole === 'ALL' ? 'active' : ''}`}
            onClick={() => setFilterRole('ALL')}
          >
            All
          </button>
          <button
            className={`filter-btn ${filterRole === 'ADMIN' ? 'active' : ''}`}
            onClick={() => setFilterRole('ADMIN')}
          >
            Admins
          </button>
          <button
            className={`filter-btn ${filterRole === 'USER' ? 'active' : ''}`}
            onClick={() => setFilterRole('USER')}
          >
            Users
          </button>
        </div>
      </div>

      {/* Users Table */}
      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Username</th>
              <th>Email</th>
              <th>Name</th>
              <th>Role</th>
              <th>Status</th>
              <th>Email Verified</th>
              <th>Phone Verified</th>
              <th>Free AI Limit</th>
              <th>Created At</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.length === 0 ? (
              <tr>
                <td colSpan="10" className="empty-state">
                  No users found
                </td>
              </tr>
            ) : (
              filteredUsers.map(user => (
                <tr key={user.id}>
                  <td>{user.id}</td>
                  <td className="username-cell">{user.username || 'N/A'}</td>
                  <td className="email-cell">{user.email || 'N/A'}</td>
                  <td>
                    {user.firstName || user.lastName 
                      ? `${user.firstName || ''} ${user.lastName || ''}`.trim()
                      : 'N/A'}
                  </td>
                  <td>{getRoleBadge(user.role || 'USER')}</td>
                  <td>
                    <span className={`status-badge ${user.isActive ? 'active' : 'inactive'}`}>
                      {user.isActive ? '✓ Active' : '✗ Inactive'}
                    </span>
                  </td>
                  <td>
                    <span className={`verification-badge ${user.isEmailVerified ? 'verified' : 'unverified'}`}>
                      {user.isEmailVerified ? '✓' : '✗'}
                    </span>
                  </td>
                  <td>
                    <span className={`verification-badge ${user.isPhoneVerified ? 'verified' : 'unverified'}`}>
                      {user.isPhoneVerified ? '✓' : '✗'}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                      <input
                        type="number"
                        value={getUserLimitValue(user)}
                        onChange={(e) => setUserLimitEdits(prev => ({
                          ...prev,
                          [user.id]: e.target.value
                        }))}
                        placeholder="Global"
                        style={{ width: '70px', padding: '6px' }}
                      />
                      <button
                        className="filter-btn"
                        onClick={() => saveUserLimit(user.id)}
                        disabled={savingUserId === user.id}
                        style={{ padding: '6px 10px' }}
                      >
                        Save
                      </button>
                      <button
                        className="filter-btn"
                        onClick={() => {
                          setUserLimitEdits(prev => ({ ...prev, [user.id]: '' }));
                          saveUserLimit(user.id, '');
                        }}
                        disabled={savingUserId === user.id}
                        style={{ padding: '6px 10px' }}
                      >
                        Clear
                      </button>
                    </div>
                  </td>
                  <td>{formatDate(user.createdAt)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="table-footer">
        <p>Showing {filteredUsers.length} of {users.length} users</p>
      </div>
    </div>
  );
};

export default UsersManagement;

